<template>
  <SrpDetailsSummary class="collab-contract-details-wrap" :isOpened="true">
    <template #heading>
      <!-- Spoiler header -->
      <div class="spoiler-header" style="width: 100%">
        <h2 class="global-h2 spoiler-header__title">
          <span><span>Work Agreement</span></span>
        </h2>

        <SrpSwitch v-if="props.customCollabClientPayload.creatorCollabInputs.agreedToDate" class="spoiler-header__switch" v-model:isChecked="isSnapshotVisible">Show original agreement</SrpSwitch>
      </div>
      <!-- / Spoiler header -->
    </template>

    <template #details>
      <div style="margin-bottom: 8px; border-bottom: 1px rgba(0, 0, 0, 0.15) solid"></div>

      <!-- Collab Details -->
      <div
        :class="{
          'collab-contract-details': true,
          'collab-contract-details--disabled': isLoadingSnapshot,
        }"
      >
        <div v-if="isLoadingSnapshot" class="collab-contract-details__loader-icon-wrap">
          <Loader class="collab-contract-details__loader-icon" />
        </div>

        <NoteWithIcon v-if="!customerCollabInputs.formalizedDate" color="blue" size="tiny" style="margin-bottom: 10px">
          <template #text><b>The destination will fill this in and request your agreement.</b></template>
        </NoteWithIcon>

        <!-- Title and content snippet -->
        <div class="title-and-content-snippet collab-contract-details__title-and-content-snippet">
          <div v-if="customerCollabInputs.title" class="global-h4 title-and-content-snippet__title">{{ customerCollabInputs.title }}</div>
          <div v-else class="global-h4 title-and-content-snippet__title">What's the purpose of the work?</div>
          <div class="title-and-content-snippet__content">
            {{ customerCollabInputs.purpose }}
          </div>
        </div>
        <!-- / Title and content snippet -->

        <div class="collab-contract-details__horizontal-divider"></div>

        <!-- Title and content snippet -->
        <div class="title-and-content-snippet collab-contract-details__title-and-content-snippet">
          <div class="global-h4 title-and-content-snippet__title">Deliverables</div>
          <div class="title-and-content-snippet__content">
            {{ customerCollabInputs.deliverables }}
          </div>
        </div>
        <!-- / Title and content snippet -->

        <div class="collab-contract-details__horizontal-divider"></div>

        <!-- Title and content snippet -->
        <div class="title-and-content-snippet collab-contract-details__title-and-content-snippet collab-contract-details__title-and-content-snippet--50-percent-width">
          <div class="global-h4 title-and-content-snippet__title">Payment</div>
          <div class="title-and-content-snippet__content">
            <b v-if="customerCollabInputs.paymentAmount">$ {{ customerCollabInputs.paymentAmount }}</b>
            <b v-else>--</b>
          </div>
        </div>
        <!-- / Title and content snippet -->

        <div class="collab-contract-details__vertical-divider"></div>

        <!-- Title and content snippet -->
        <div class="title-and-content-snippet collab-contract-details__title-and-content-snippet collab-contract-details__title-and-content-snippet--50-percent-width">
          <div class="global-h4 title-and-content-snippet__title">Other Compensation</div>
          <div class="title-and-content-snippet__content" style="display: block">
            <template v-for="(comp, index) in customerCollabInputs.otherComp" :key="index">
              <b>{{ comp }}</b>
              <br />
            </template>
          </div>
        </div>
        <!-- / Title and content snippet -->

        <div class="collab-contract-details__horizontal-divider"></div>

        <!-- Title and content snippet -->
        <div class="title-and-content-snippet collab-contract-details__title-and-content-snippet collab-contract-details__title-and-content-snippet--50-percent-width">
          <div class="global-h4 title-and-content-snippet__title">Due Date</div>
          <div class="title-and-content-snippet__content">
            <IconEmbedded name="calendar_2" :size="20" color="rgba(0, 0, 0, 0.5)" style="margin-right: 6px" />
            <b v-if="customerCollabInputs.dueDate">{{ moment(customerCollabInputs.dueDate).format("MMM Do, YYYY") }}</b>
          </div>
        </div>
        <!-- / Title and content snippet -->

        <div class="collab-contract-details__vertical-divider"></div>

        <!-- Title and content snippet -->
        <div class="title-and-content-snippet collab-contract-details__title-and-content-snippet collab-contract-details__title-and-content-snippet--50-percent-width">
          <div class="global-h4 title-and-content-snippet__title">Activity Date</div>
          <div class="title-and-content-snippet__content">
            <IconEmbedded name="calendar_2" :size="20" color="rgba(0, 0, 0, 0.5)" style="margin-right: 6px" />
            <b v-if="customerCollabInputs.activityDate">{{ moment(customerCollabInputs.activityDate).format("MMM Do, YYYY") }}</b>
          </div>
        </div>
        <!-- / Title and content snippet -->

        <div class="collab-contract-details__horizontal-divider"></div>

        <!-- Title and content snippet -->
        <div class="title-and-content-snippet collab-contract-details__title-and-content-snippet">
          <div class="global-h4 title-and-content-snippet__title">Additional Notes</div>
          <div class="title-and-content-snippet__content">
            <template v-if="customerCollabInputs.otherNotes">
              {{ customerCollabInputs.otherNotes }}
            </template>
            <span v-else>--</span>
          </div>
        </div>
        <!-- / Title and content snippet -->

        <NoteWithIcon v-if="isSnapshotVisible" color="gray" size="tiny" style="width: 100%; margin-bottom: 10px">
          <template #text>
            <b>These details were agreed to on {{ moment(props.customCollabClientPayload.creatorCollabInputs.agreedToDate).format("MMM Do, YYYY") }}</b>
            <!--<span> If you have questions send a message to <CopyTextNew>support@shrpa.com</CopyTextNew></span>-->
          </template>
        </NoteWithIcon>
      </div>
      <!-- / Collab Details -->
    </template>
  </SrpDetailsSummary>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import moment from "moment";
import CreatorCustomCollabsRepo from "@repos/CreatorCustomCollabsRepo";

// Components
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SrpSwitch from "@components/ui/SrpSwitch.vue";
import CopyTextNew from "@components/CopyTextNew.vue";

// Types
import { CustomCollabClientPayload, CustomCollabCustomerData } from "@contracts/customCollabs";
import Loader from "@components/Loader/Loader.vue";

const props = withDefaults(
  defineProps<{
    customCollabClientPayload: CustomCollabClientPayload | null;
  }>(),
  {
    customCollabClientPayload: null,
  }
);

// Toggle collab details snapshot =============================================
const isSnapshotVisible = ref<boolean>(false);
const customerCollabInputsSnapshot = ref<CustomCollabCustomerData | null>(null);
const isLoadingSnapshot = ref<boolean>(false);

const customerCollabInputs = computed<CustomCollabCustomerData | null>(() => {
  if (isSnapshotVisible.value && customerCollabInputsSnapshot.value) {
    return customerCollabInputsSnapshot.value;
  } else {
    return props.customCollabClientPayload.customerCollabInputs;
  }
});

// Load the snapshot
watch(isSnapshotVisible, async () => {
  if (isSnapshotVisible.value && !customerCollabInputsSnapshot.value) {
    const creatorId = props.customCollabClientPayload.creatorCollabInputs.creatorId;
    const customerId = props.customCollabClientPayload.customerCollabInputs.customerId;
    const customCollabId = props.customCollabClientPayload.customerCollabInputs.id;

    isLoadingSnapshot.value = true;
    customerCollabInputsSnapshot.value = await CreatorCustomCollabsRepo.loadCustomerCollabInputsSnapshot(creatorId, customerId, customCollabId);
    isLoadingSnapshot.value = false;
  }
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

// Spoiler header =============================================================
.spoiler-header {
  gap: 10px 15px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;

  &__title {
  }

  &__switch {
  }
}

// Title and content snippet ==================================================
.title-and-content-snippet {
  &__title {
    margin-bottom: 7px;
  }

  &__content {
    @include standardFont;
    display: flex;
    align-items: center;
    white-space: pre-wrap;
  }
}

// Collab Details ====================================================
.collab-contract-details {
  display: flex;
  flex-wrap: wrap;
  position: relative;
  z-index: 0;

  &--disabled {
    opacity: 0.3;
  }

  &__loader-icon-wrap {
    width: 1px;
    height: 100%;
    position: absolute;
    inset: 0 auto auto 50%;
  }

  &__loader-icon {
    width: 50px;
    height: 50px;
    margin: 20px 0 0 -25px;
    position: sticky;
    inset: 50px auto auto auto;
  }

  &__note-with-icon {
    margin-bottom: 20px;
  }

  &__title-and-content-snippet {
    margin-bottom: 20px;
    box-sizing: border-box;

    &--50-percent-width {
      width: 50%;
    }
  }

  &__vertical-divider {
    width: 0;
    margin-bottom: 20px;
    position: relative;

    &::before {
      content: "";
      width: 1px;
      height: 100%;
      border-left: 1px rgba(0, 0, 0, 0.1) solid;
      position: absolute;
      inset: 0 auto auto -20px;
    }
  }

  &__horizontal-divider {
    width: 100%;
    margin-bottom: 20px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }
}

// Collab Details wrap ===============================================
.collab-contract-details-wrap {
  padding: 20px 25px 24px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 3px 15px -5px rgba(0, 0, 0, 0.4);
}
</style>
